import type {
  HostingStatus,
  PartnerLedgerEntry,
  PartnerLedgerMethod,
  PutSitePartnersInput,
  SitePartners,
} from "@lumora/bridge";
import { formatUsd } from "@lumora/core";

export const FULL_BPS = 10_000;

/** One editable row in the split editor. `partnerSlug === null` is the fixed Platform row. */
export type SplitRow = {
  key: string;
  partnerSlug: string | null;
  percent: string;
};

/** "33.33" -> 3333. Returns null for blank / invalid / negative input. */
export function percentToBps(text: string): number | null {
  const t = text.trim();
  if (!/^\d+(\.\d{0,2})?$/.test(t)) return null;
  return Math.round(Number.parseFloat(t) * 100);
}

export function bpsToPercent(bps: number): string {
  const p = bps / 100;
  return Number.isInteger(p) ? String(p) : p.toFixed(2).replace(/0$/, "");
}

export function rowsFromSitePartners(sp: SitePartners): SplitRow[] {
  const rows: SplitRow[] = [];
  let platform: SplitRow | null = null;
  sp.participants.forEach((p, i) => {
    const row: SplitRow = {
      key: p.partnerSlug ?? `platform-${i}`,
      partnerSlug: p.partnerSlug ?? null,
      percent: bpsToPercent(p.percentBps),
    };
    if (p.partnerSlug) rows.push(row);
    else platform = row;
  });
  return [
    platform ?? { key: "platform", partnerSlug: null, percent: "100" },
    ...rows,
  ];
}

export type SplitCheck = {
  totalBps: number;
  valid: boolean;
  problem: string | null;
};

export function checkSplit(rows: SplitRow[]): SplitCheck {
  let total = 0;
  for (const r of rows) {
    const bps = percentToBps(r.percent);
    if (bps === null) {
      return {
        totalBps: total,
        valid: false,
        problem: "Enter every percentage as a number (up to 2 decimals).",
      };
    }
    total += bps;
  }
  if (rows.some((r) => r.partnerSlug === "")) {
    return {
      totalBps: total,
      valid: false,
      problem: "Choose a partner for every partner row.",
    };
  }
  const slugs = rows
    .filter((r) => r.partnerSlug !== null)
    .map((r) => r.partnerSlug);
  if (new Set(slugs).size !== slugs.length) {
    return {
      totalBps: total,
      valid: false,
      problem: "Each partner can appear only once.",
    };
  }
  if (total !== FULL_BPS) {
    return {
      totalBps: total,
      valid: false,
      problem: "Shares must equal 100%.",
    };
  }
  return { totalBps: total, valid: true, problem: null };
}

export function buildPutInput(
  rows: SplitRow[],
  autoSplit: boolean,
): PutSitePartnersInput {
  return {
    participants: rows.map((r) =>
      r.partnerSlug === null
        ? { platform: true as const, percentBps: percentToBps(r.percent) ?? 0 }
        : {
            partnerSlug: r.partnerSlug,
            percentBps: percentToBps(r.percent) ?? 0,
          },
    ),
    autoSplit,
  };
}

/** Partner slugs in the split whose Stripe onboarding is not complete. */
export function partnersNotReady(
  rows: SplitRow[],
  ready: Record<string, boolean>,
): string[] {
  return rows
    .filter((r) => r.partnerSlug && ready[r.partnerSlug] !== true)
    .map((r) => r.partnerSlug as string);
}

export function hostingStatusLines(h: HostingStatus) {
  const card = h.cardOnFile === null ? "Unknown" : h.cardOnFile ? "Yes" : "No";
  const invoice = h.lastInvoice
    ? `${h.lastInvoice.status} · ${formatUsd(h.lastInvoice.amountCents)}${
        h.lastInvoice.paidAt
          ? ` · paid ${new Date(h.lastInvoice.paidAt).toLocaleDateString()}`
          : " · not paid"
      }`
    : "No invoices yet";
  const timing = h.pastDue
    ? "Past due"
    : h.onTime === true
      ? "On time"
      : "Not yet known";
  return {
    subscription: h.subscriptionStatus || "—",
    lifecycle: h.lifecycle || "—",
    card,
    invoice,
    timing,
  };
}

const METHOD_LABEL: Record<PartnerLedgerMethod, string> = {
  cash: "Cash",
  check: "Check",
  wire: "Wire",
  stripe_transfer: "Stripe transfer",
  other: "Other",
};

export const LEDGER_COLUMNS = [
  "When",
  "Direction",
  "Amount",
  "Method",
  "Source",
  "Site",
  "Note",
];

export function ledgerRows(entries: PartnerLedgerEntry[]): string[][] {
  return entries.map((e) => [
    new Date(e.occurredAt).toLocaleDateString(),
    e.direction === "to_partner" ? "I paid partner" : "Partner paid me",
    formatUsd(e.amountCents),
    METHOD_LABEL[e.method] ?? e.method,
    e.source === "auto" ? "Auto" : "Manual",
    e.siteSlug || "—",
    e.note || "—",
  ]);
}

/** Balance sentence; net > 0 means the platform has paid the partner more than it received. */
export function balanceLine(netToPartnerCents: number): {
  line: string;
  explain: string;
} {
  const abs = formatUsd(Math.abs(netToPartnerCents));
  return {
    line: `Net paid to partner: ${netToPartnerCents < 0 ? "-" : ""}${abs}`,
    explain:
      netToPartnerCents > 0
        ? "Positive: you have paid the partner more than they have paid you."
        : netToPartnerCents < 0
          ? "Negative: the partner has paid you more than you have paid them."
          : "Zero: payments in both directions are even.",
  };
}

/** Dollar text -> cents; null when invalid or not positive. */
export function dollarsToCents(text: string): number | null {
  const n = Number.parseFloat(text);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}
